import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataService } from "@/services/data";
import { apiErrorMessage } from "@/utils/api-error";
import { Check, Copy, Download, ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

type Stage = "module" | "yoda";
type Piece = { type: string; label: string; deposited: boolean; can_deposit: boolean };
type Dossier = {
    id: number;
    stage: Stage;
    name: string;
    email: string | null;
    link: { label: string; url: string };
    missing: string[];
    sheet: { label: string; value: string }[];
    documents: { index: number; name: string; status: { label: string; tone: BadgeTone } }[];
    yoda_number?: string;
    passport?: string;
    pieces?: Piece[];
    badge: { tone: BadgeTone; label: string; detail?: string } | null;
};
type Urgency = { late: number; urgent: number; on_time: number; no_deadline: number };
type Page = { counts: Record<Stage, number>; urgency: Record<Stage, Urgency>; held: Dossier | null };

const URL = "/registration/sales/iperia-operator/";
const QUEUES: Record<Stage, string> = { module: "Parcours module à créer", yoda: "Assmat à inscrire sur Yoda" };

const post = <T,>(path: string, body: object) => DataService.client.url(URL + path).post(body).json<T>();

async function download(path: string, filename: string) {
    try {
        const blob = await DataService.client.url(URL + path).get().blob();
        const anchor = document.createElement("a");
        anchor.href = window.URL.createObjectURL(blob);
        anchor.download = filename;
        anchor.click();
        window.URL.revokeObjectURL(anchor.href);
    } catch {
        toast.error("Impossible de télécharger le document.");
    }
}

export function IperiaOperationsPage() {
    const [page, setPage] = useState<Page | null>(null);
    const [skipped, setSkipped] = useState<number[]>([]);
    const [busy, setBusy] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);

    const load = () =>
        DataService.client
            .url(URL)
            .get()
            .json<Page>()
            .then(response => {
                setPage(response);
                setLoadFailed(false);
            });

    useEffect(() => {
        load().catch(() => setLoadFailed(true));
    }, []);

    const run = async (work: () => Promise<void>) => {
        setBusy(true);
        try {
            await work();
        } catch (error) {
            toast.error(apiErrorMessage(error, "L'opération a échoué."));
        } finally {
            setBusy(false);
        }
    };

    const claim = (stage: Stage, exclude: number[] = skipped) =>
        run(async () => {
            const next = await post<Page>("claim/", { stage, exclude_ids: exclude });
            setPage(next);
            if (!next.held) toast.info("Plus aucun dossier libre dans cette file.");
        });

    const leave = (dossier: Dossier, skippedIt: boolean) =>
        run(async () => {
            const released = await post<Page>(`${dossier.id}/release/`, { skipped: skippedIt });
            if (!skippedIt) return setPage(released);
            const exclude = [...skipped, dossier.id];
            setSkipped(exclude);
            setPage(await post<Page>("claim/", { stage: dossier.stage, exclude_ids: exclude }));
        });

    const saved = (dossier: Dossier) => async (result: { forest_sync: string }) => {
        if (result.forest_sync === "failed") toast.warning("Enregistré dans le CRM. La copie vers Forest n'est pas partie : l'équipe tech est prévenue.");
        else toast.success("Enregistré.");
        // A Yoda dossier saved without « Inscrite » stays in its queue: do not hand it straight back.
        const exclude = [...skipped, dossier.id];
        setSkipped(exclude);
        setPage(await post<Page>("claim/", { stage: dossier.stage, exclude_ids: exclude }));
    };

    if (!page)
        return (
            <main className="space-y-3 p-6 text-sm text-muted-foreground">
                <p>{loadFailed ? "Impossible de charger la file IPERIA." : "Chargement…"}</p>
                {loadFailed && (
                    <Button variant="outline" onClick={() => load().catch(() => toast.error("Impossible de charger la file IPERIA."))}>
                        Réessayer
                    </Button>
                )}
            </main>
        );

    const held = page.held;
    return (
        <main className="mx-auto max-w-5xl space-y-6 p-6">
            <h1 className="text-2xl font-semibold">Inscriptions IPERIA</h1>
            {!held ? (
                <div className="grid gap-4 md:grid-cols-2">
                    {(Object.keys(QUEUES) as Stage[]).map(stage => (
                        <Card key={stage}>
                            <CardHeader>
                                <CardTitle>{QUEUES[stage]}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-3xl font-semibold">{page.counts[stage]}</span>
                                    <Button onClick={() => claim(stage)} disabled={busy || page.counts[stage] === 0}>
                                        Commencer
                                    </Button>
                                </div>
                                <QueueBadges urgency={page.urgency[stage]} />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <Card key={held.id}>
                    <CardHeader>
                        <CardTitle>
                            {QUEUES[held.stage]} — {held.name}
                        </CardTitle>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>{held.email}</span>
                            {held.badge && (
                                <Badge tone={held.badge.tone}>
                                    {held.badge.label}
                                    {held.badge.detail ? ` · ${held.badge.detail}` : ""}
                                </Badge>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {held.missing.length > 0 && <p className="rounded-md bg-red-100 p-3 text-sm text-red-800">Il manque : {held.missing.join(", ")}.</p>}
                        <Button variant="outline" onClick={() => window.open(held.link.url, "_blank", "noopener,noreferrer")}>
                            {held.link.label}
                            <ExternalLink className="ml-2 size-4" />
                        </Button>
                        <div className="grid gap-3 md:grid-cols-2">
                            {held.sheet.map(item => (
                                <CopyValue key={item.label} {...item} />
                            ))}
                        </div>
                        {held.stage === "yoda" && <Documents dossier={held} />}
                        {held.stage === "module" ? (
                            <ModuleForm dossier={held} busy={busy} run={run} onSaved={saved(held)} />
                        ) : (
                            <YodaForm dossier={held} busy={busy} run={run} onSaved={saved(held)} />
                        )}
                        <div className="flex gap-2 border-t pt-4">
                            <Button variant="outline" disabled={busy} onClick={() => leave(held, true)}>
                                Passer ce dossier
                            </Button>
                            <Button variant="ghost" disabled={busy} onClick={() => leave(held, false)}>
                                Quitter
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}
        </main>
    );
}

function QueueBadges({ urgency }: { urgency: Urgency }) {
    const items = [
        urgency.late > 0 && { tone: "danger" as const, label: `En retard · ${urgency.late}` },
        urgency.urgent > 0 && { tone: "warning" as const, label: `Urgent · ${urgency.urgent}` },
        urgency.on_time > 0 && { tone: "info" as const, label: `Dans les temps · ${urgency.on_time}` },
        urgency.no_deadline > 0 && { tone: "neutral" as const, label: `Sans début OPCO · ${urgency.no_deadline}` }
    ].filter(item => item !== false);
    if (!items.length) return null;
    return (
        <div className="flex flex-wrap gap-2">
            {items.map(item => (
                <Badge key={item.label} tone={item.tone}>
                    {item.label}
                </Badge>
            ))}
        </div>
    );
}

type FormProps = {
    dossier: Dossier;
    busy: boolean;
    run: (work: () => Promise<void>) => Promise<void>;
    onSaved: (result: { forest_sync: string }) => Promise<void>;
};

function ModuleForm({ dossier, busy, run, onSaved }: FormProps) {
    const [moduleNumber, setModuleNumber] = useState("");
    const submit = () => run(async () => onSaved(await post(`${dossier.id}/module-number/`, { module_number: moduleNumber })));
    return (
        <div className="space-y-2">
            <Label htmlFor="module-number">N° parcours module</Label>
            <div className="flex gap-2">
                <Input id="module-number" value={moduleNumber} onChange={event => setModuleNumber(event.target.value)} />
                <Button onClick={submit} disabled={busy || !moduleNumber.trim()}>
                    Enregistrer et passer au suivant
                </Button>
            </div>
        </div>
    );
}

function YodaForm({ dossier, busy, run, onSaved }: FormProps) {
    const [yodaNumber, setYodaNumber] = useState(dossier.yoda_number ?? "");
    const [passport, setPassport] = useState(dossier.passport ?? "");
    const [enrolled, setEnrolled] = useState(false);
    const [deposited, setDeposited] = useState<string[]>([]);
    const toggle = (type: string, checked: boolean) => setDeposited(current => (checked ? [...current, type] : current.filter(item => item !== type)));
    const submit = () =>
        run(async () => onSaved(await post(`${dossier.id}/yoda/`, { yoda_number: yodaNumber, passport, enrolled, deposited })));
    return (
        <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                    <Label htmlFor="yoda-number">N° YODA</Label>
                    <Input id="yoda-number" value={yodaNumber} onChange={event => setYodaNumber(event.target.value)} />
                </div>
                <div className="space-y-1">
                    <Label htmlFor="passport">N° passeport formation</Label>
                    <Input id="passport" maxLength={9} value={passport} onChange={event => setPassport(event.target.value)} />
                </div>
            </div>
            <CheckRow id="enrolled" label="Inscrite sur Iperia" checked={enrolled} onChange={setEnrolled} />
            {dossier.pieces?.map(piece => (
                <CheckRow
                    key={piece.type}
                    id={piece.type}
                    label={piece.label}
                    checked={piece.deposited || deposited.includes(piece.type)}
                    disabled={piece.deposited || !piece.can_deposit}
                    onChange={checked => toggle(piece.type, checked)}
                />
            ))}
            <Button onClick={submit} disabled={busy || !yodaNumber.trim() || !passport.trim()}>
                Enregistrer et passer au suivant
            </Button>
        </div>
    );
}

function CheckRow({ id, label, checked, disabled = false, onChange }: { id: string; label: string; checked: boolean; disabled?: boolean; onChange: (checked: boolean) => void }) {
    return (
        <div className="flex items-center gap-2">
            <Checkbox id={id} checked={checked} disabled={disabled} onCheckedChange={value => onChange(value === true)} />
            <Label htmlFor={id} className={disabled ? "text-muted-foreground" : undefined}>
                {label}
            </Label>
        </div>
    );
}

function Documents({ dossier }: { dossier: Dossier }) {
    return (
        <div className="space-y-2">
            <Button variant="outline" size="sm" onClick={() => download(`${dossier.id}/documents/`, `documents-${dossier.id}.zip`)}>
                <Download className="mr-2 size-4" />
                Télécharger tous les documents
            </Button>
            {dossier.documents.map(document => (
                <div key={document.index} className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm">{document.name}</span>
                        <Badge tone={document.status.tone}>{document.status.label}</Badge>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => download(`${dossier.id}/documents/?index=${document.index}`, document.name)}>
                        <Download className="mr-2 size-4" />
                        Télécharger
                    </Button>
                </div>
            ))}
        </div>
    );
}

function CopyValue({ label, value }: { label: string; value: string }) {
    const [copied, setCopied] = useState(false);
    return (
        <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
            <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="truncate font-medium">{value || "Non renseigné"}</p>
            </div>
            {value && (
                <Button type="button" variant="outline" size="sm" aria-label={`Copier ${label}`} onClick={() => navigator.clipboard.writeText(value).then(() => setCopied(true))}>
                    {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                </Button>
            )}
        </div>
    );
}
